import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { DateRange } from '@/types'

// Connection status types for API and WebSocket
export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'error'

export interface ConnectionState {
  api: ConnectionStatus
  websocket: ConnectionStatus
  lastApiCheck: string | null
  lastError: string | null
}

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean
  toggleSidebar: () => void

  // Theme
  theme: 'dark' | 'light'
  setTheme: (theme: 'dark' | 'light') => void

  // Live mode
  isLiveMode: boolean
  setLiveMode: (isLive: boolean) => void
  refreshInterval: number
  setRefreshInterval: (interval: number) => void

  // Connection status - for live indicator and offline detection
  connectionStatus: ConnectionState
  setApiStatus: (status: ConnectionStatus, error?: string) => void
  setWebSocketStatus: (status: ConnectionStatus) => void
  isOnline: () => boolean

  // Date range filter
  dateRange: DateRange
  setDateRange: (range: DateRange) => void

  // Modals
  activeModal: string | null
  modalData: unknown
  openModal: (modalId: string, data?: unknown) => void
  closeModal: () => void

  // Notifications
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void
}

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  timestamp: string
  duration?: number
}

const defaultDateRange: DateRange = {
  start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  end: new Date().toISOString(),
  label: 'Last 7 days'
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Sidebar
        sidebarCollapsed: false,
        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        // Theme
        theme: 'dark',
        setTheme: (theme) => set({ theme }),

        // Live mode
        isLiveMode: true,
        setLiveMode: (isLive) => set({ isLiveMode: isLive }),
        refreshInterval: 30000, // 30 seconds
        setRefreshInterval: (interval) => set({ refreshInterval: interval }),

        // Connection status - tracks API and WebSocket connection state
        connectionStatus: {
          api: 'disconnected',
          websocket: 'disconnected',
          lastApiCheck: null,
          lastError: null
        },
        setApiStatus: (status, error) =>
          set((state) => ({
            connectionStatus: {
              ...state.connectionStatus,
              api: status,
              lastApiCheck: new Date().toISOString(),
              lastError: error || null
            }
          })),
        setWebSocketStatus: (status) =>
          set((state) => ({
            connectionStatus: {
              ...state.connectionStatus,
              websocket: status
            }
          })),
        isOnline: () => {
          const { connectionStatus } = get()
          return connectionStatus.api === 'connected'
        },

        // Date range
        dateRange: defaultDateRange,
        setDateRange: (range) => set({ dateRange: range }),

        // Modals
        activeModal: null,
        modalData: null,
        openModal: (modalId, data) =>
          set({ activeModal: modalId, modalData: data }),
        closeModal: () => set({ activeModal: null, modalData: null }),

        // Notifications
        notifications: [],
        addNotification: (notification) =>
          set((state) => ({
            notifications: [
              {
                ...notification,
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString()
              },
              ...state.notifications
            ].slice(0, 10) // Keep only last 10
          })),
        removeNotification: (id) =>
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id)
          })),
        clearNotifications: () => set({ notifications: [] })
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
          refreshInterval: state.refreshInterval
        })
      }
    ),
    { name: 'ui-store' }
  )
)
